import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ScenePreview } from "@/components/studio/ScenePreview";
import { CollectionCard } from "@/components/studio/CollectionCard";
import { RenderPanel } from "@/components/studio/RenderPanel";

export default function Studio() {
  const { logout } = useAuth();
  const [scene, setScene] = useState(null);
  const [photos, setPhotos] = useState([]);

  const loadPhotos = useCallback(() => api.get("/photos").then((r) => setPhotos(r.data)), []);
  useEffect(() => { api.get("/scene").then((r) => setScene(r.data)); loadPhotos(); }, [loadPhotos]);

  const upsert = (p) => setPhotos((ps) => [...ps.filter((x) => !(x.collection === p.collection && x.slot === p.slot)), p]);
  const remove = (id) => setPhotos((ps) => ps.filter((x) => x.id !== id));

  if (!scene) return <div className="min-h-screen grid place-items-center eyebrow">Cargando…</div>;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-[#1C1715]/10 bg-[#FDFBF7]/85 backdrop-blur-xl px-6 sm:px-10 py-4">
        <span className="font-serif-display text-xl tracking-[0.3em]">LUMINA <span className="text-xs tracking-[0.4em] text-[#8C827A]">ESTUDIO</span></span>
        <div className="flex items-center gap-6 text-xs uppercase tracking-[0.2em]">
          <Link to="/studio/pedidos" data-testid="studio-orders-link" className="text-[#5C534C] hover:text-[#1C1715] transition-colors">Pedidos</Link>
          <Link to="/studio/fotos" data-testid="studio-site-media-link" className="text-[#5C534C] hover:text-[#1C1715] transition-colors">Contenido del sitio</Link>
          <Link to="/studio/inspiracion" data-testid="studio-inspiration-link" className="text-[#5C534C] hover:text-[#1C1715] transition-colors">Inspiración</Link>
          <Link to="/studio/disenos" data-testid="studio-designs-link" className="text-[#5C534C] hover:text-[#1C1715] transition-colors">Diseños</Link>
          <Link to="/studio/catalogo" data-testid="studio-catalog-link" className="text-[#5C534C] hover:text-[#1C1715] transition-colors">Catálogo</Link>
          <Link to="/" data-testid="view-homepage-link" className="flex items-center gap-2 text-[#5C534C] hover:text-[#1C1715] transition-colors">Portada <ExternalLink className="h-3.5 w-3.5" /></Link>
          <button data-testid="logout-button" onClick={logout} className="flex items-center gap-2 text-[#5C534C] hover:text-[#1C1715] transition-colors">Salir <LogOut className="h-3.5 w-3.5" /></button>
        </div>
      </header>

      <main className="grid gap-12 px-6 sm:px-10 py-10 xl:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
        <div className="xl:sticky xl:top-24 self-start space-y-8">
          <div>
            <p className="eyebrow mb-3">Vista previa en vivo</p>
            <h1 className="font-serif-display text-3xl sm:text-4xl font-light text-[#1C1715]">El salón, con tus fotografías</h1>
          </div>
          <ScenePreview scene={scene} photos={photos} />
          <RenderPanel photos={photos} />
        </div>
        <div className="space-y-6">
          {scene.collections.map((c, i) => (
            <CollectionCard key={c.id} index={i} collection={c} scene={scene} photos={photos} onUpsert={upsert} onRemove={remove} />
          ))}
        </div>
      </main>
    </div>
  );
}
